import {
  Form,
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
  useRefresh,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { useWatch } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
  computeSaleSplit,
  type SaleSplitOutcome,
} from "../corretaje/corretajeSale";
import { ListCardsSkeleton, ListErrorState } from "../design/ListStates";
import type { CrmDataProvider } from "../providers/types";
import type {
  AppRole,
  CorretajeCommissionScheme,
  CorretajeSaleAdministration,
  OperationSummary,
  PropertyOption,
  Sale,
} from "../types";
import {
  advisorLabel,
  FormFields,
  toFormValues,
  toSaleInput,
  type Context,
  type FormValues,
} from "./CorretajeSaleFields";
import { SaleSummary } from "./CorretajeSaleSummary";
import { CorretajeSplitPanel } from "./CorretajeSplitPanel";
import { CorretajeVoboBlock } from "./CorretajeVoboBlock";
import { canEditCorretajeSale, todayIso } from "./operationRules";

/** Live split while the fields change: the TS mirror of the SQL calculator, no request per keystroke. */
const LiveSplit = ({
  option,
  vendedoraId,
  schemes,
}: {
  option: PropertyOption;
  vendedoraId: OperationSummary["sales_id"];
  schemes: CorretajeCommissionScheme[];
}) => {
  const translate = useTranslate();
  const values = useWatch() as FormValues;
  const input = toSaleInput(values);
  const outcome: SaleSplitOutcome | null =
    schemes.length === 0
      ? null
      : input.commission_total == null
        ? { ok: false, message: translate("crm.corretaje.sale.split_empty") }
        : computeSaleSplit(input, option, vendedoraId, schemes, todayIso());
  return <CorretajeSplitPanel outcome={outcome} isSnapshot={false} />;
};

const SaleForm = ({
  context,
  onSaved,
}: {
  context: Context;
  onSaved: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { operation, admin, option, schemes } = context;

  const save = async (values: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await dataProvider.saveCorretajeSale(
        operation.id,
        toSaleInput(values as FormValues),
      );
      notify("crm.corretaje.sale.saved", { type: "success" });
      onSaved();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : translate("crm.operations.error"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Form record={toFormValues(admin)} onSubmit={save} className="space-y-4">
      <FormFields context={context} />
      <LiveSplit
        option={option}
        vendedoraId={operation.sales_id}
        schemes={schemes}
      />
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <Button type="submit" className="min-h-11" disabled={busy}>
        {translate("crm.corretaje.sale.save")}
      </Button>
    </Form>
  );
};

/**
 * Almikia Epic EC (TASK-032): the "Administracion de la venta" card of a
 * corretaje operation plus its Direccion VoBo block. Fields follow
 * docs/almikia/corretaje-commissions-2026-09-28.md section 3: shared with a
 * colega (then the colega and who found her), whose property, the vendedora,
 * and the total commission; the split and its case number update live. The
 * form is editable by direccion, administracion and lead_manager until the
 * VoBo is approved; everyone else, and an approved sale, see it read-only. The
 * server functions stay the authority for every rule.
 */
export const CorretajeSaleAdmin = ({
  operation,
  role,
}: {
  operation: OperationSummary;
  role: AppRole;
}) => {
  const translate = useTranslate();
  const refresh = useRefresh();
  const adminQuery = useGetList<CorretajeSaleAdministration>(
    "corretaje_sale_administration",
    {
      filter: { operation_id: operation.id },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "ASC" },
    },
  );
  const optionQuery = useGetOne<PropertyOption>(
    "property_options",
    { id: operation.option_id ?? "" },
    { enabled: operation.option_id != null },
  );
  const { data: schemes } = useGetList<CorretajeCommissionScheme>(
    "corretaje_commission_schemes",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "id", order: "ASC" },
    },
  );
  const vendedoraQuery = useGetOne<Sale>("sales", { id: operation.sales_id });
  const responsableId = optionQuery.data?.responsable_sales_id;
  const { data: responsable } = useGetOne<Sale>(
    "sales",
    { id: responsableId ?? "" },
    { enabled: responsableId != null },
  );

  const admin = adminQuery.data?.[0];
  const option = optionQuery.data;
  if (
    adminQuery.isPending ||
    optionQuery.isPending ||
    vendedoraQuery.isPending
  ) {
    return <ListCardsSkeleton rows={3} />;
  }
  if (adminQuery.error || !admin || !option) {
    return (
      <ListErrorState
        message={translate("crm.corretaje.sale.error")}
        onRetry={() => adminQuery.refetch()}
      />
    );
  }

  const context: Context = {
    operation,
    admin,
    option,
    schemes: schemes ?? [],
    vendedoraText: advisorLabel(vendedoraQuery.data),
    opcionadoraText: option.responsable_is_amk
      ? "AMK"
      : advisorLabel(responsable),
  };
  const locked = admin.vobo_status === "aprobado";
  const canEdit = canEditCorretajeSale(role) && !locked;
  const vendedoraHasClave = Boolean(vendedoraQuery.data?.advisor_code?.trim());
  const onChanged = () => {
    adminQuery.refetch();
    refresh();
  };

  return (
    <div className="space-y-4" data-testid="corretaje-sale-admin">
      <Card>
        <CardContent className="space-y-4">
          <h3 className="font-semibold">
            {translate("crm.corretaje.sale.title")}
          </h3>
          {locked ? (
            <p className="text-sm text-muted-foreground">
              {translate("crm.corretaje.sale.locked_note")}
            </p>
          ) : !canEdit ? (
            <p className="text-sm text-muted-foreground">
              {translate("crm.corretaje.sale.read_only_note")}
            </p>
          ) : null}
          {canEdit ? (
            <SaleForm
              // Remount when the stored values change so the form re-syncs.
              key={admin.updated_at}
              context={context}
              onSaved={onChanged}
            />
          ) : (
            <SaleSummary context={context} />
          )}
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <CorretajeVoboBlock
            operationId={operation.id}
            admin={admin}
            role={role}
            vendedoraHasClave={vendedoraHasClave}
            onChanged={onChanged}
          />
        </CardContent>
      </Card>
    </div>
  );
};
